<template>
  <page-template :is-loading="isLoading" :data-exists="contentFilterRules !== null">
    <template #pageHeadline>
      {{ $t('contentFilterRule.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="contentFilterRules"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="content-filter-rules-table"
        :row-buttons="rowButtons"
        :new-button-link="getNewContentFilterRulePath()"
        :show-filter-row="true"
        @duplicate-row-button-clicked="duplicateContentFilterRule"
        @delete-row-button-clicked="openDeleteContentFilterRuleModal"
        @download-data="downloadDoc"
      >
      </rbz-table>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="loadContentFilterRules"
      />
      <rbz-modal-dialog
        :show="showDeleteContentFilterRuleModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteContentFilterRule"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteContentFilterRuleModal = false"
        @closeModal="showDeleteContentFilterRuleModal = false"
        size="medium"
      >
        <template #content>
          {{
            $t('common.operations.delete.modal.message', { name: selectedContentFilterRule.name })
          }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { ContentFilterRule, DocumentName } from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import _ from 'lodash';
import GitHistory from '@/components/GitHistory.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'ContentFilterRulesList',
  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },
  components: {
    GitHistory,
    PageTemplate,
    RbzModalDialog,
    RbzTable,
  },
  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('contentFilterRule.listPage.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getContentFilterRulePath(item);
          },
        },
        {
          id: 'category',
          title: this.$t('contentFilterRule.category'),
          fieldNames: ['category'],
          isSortable: true,
          isSearchable: true,
          classes: 'width-300px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'subcategory',
          title: this.$t('contentFilterRule.subcategory'),
          fieldNames: ['subcategory'],
          isSortable: true,
          isSearchable: true,
          classes: 'width-300px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'risk-level',
          title: this.$t('contentFilterRule.listPage.riskLevel'),
          fieldNames: ['risk'],
          isSortable: true,
          isSearchable: true,
          isNumber: true,
          classes: 'width-120px',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getContentFilterRulePath,
        },
        {
          id: 'duplicate',
          tooltip: this.$t('common.operations.duplicate.tableTooltip'),
          icon: 'IconDuplicate',
        },
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],
      contentFilterRules: null as ContentFilterRule[],
      pageLoadingCounter: null,

      showDeleteContentFilterRuleModal: false,
      selectedContentFilterRule: null,
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if (
          (this.$route.name as string).includes('Content Filter Rules List') &&
          val &&
          val !== oldVal
        ) {
          this.setPageLoadingStatus(true);
          await this.loadContentFilterRules();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },

  computed: {
    selectedBranch(): string {
      if (!this.branchesStore.selectedBranchId) {
        this.branchesStore.setSelectedBranch(this.branchesStore.defaultBranch);
      }
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('contentFilterRule.plural'),
        singular: this.$t('contentFilterRule.singular'),
      };
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/content-filter-rules/versions`;
    },
  },

  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    async loadContentFilterRules() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/content-filter-rules`;
      const fieldNames = _.flatMap(this.columns, 'fieldNames');
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
        config: { headers: { 'x-fields': `id, ${_.uniq(fieldNames).join(', ')}` } },
      });
      this.contentFilterRules = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    getContentFilterRulePath(contentFilterRule: ContentFilterRule) {
      return this.getEditPath('content-filter-rules', contentFilterRule.id);
    },

    editContentFilterRule(contentFilterRule: ContentFilterRule) {
      this.navigateTo(this.getContentFilterRulePath(contentFilterRule));
    },

    async duplicateContentFilterRule(contentFilterRule: ContentFilterRule) {
      this.setPageLoadingStatus(true);
      const getOriginalResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/content-filter-rules/${contentFilterRule.id}`,
      });
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      if (!getOriginalResponse?.data) {
        Utils.toast({
          message: failureMessage,
          type: 'is-danger',
        });
        return;
      }
      const originalContentFilterRule = getOriginalResponse.data;
      const contentFilterRuleToAdd = _.cloneDeep(originalContentFilterRule);
      contentFilterRuleToAdd.id = DatasetsUtils.generateUUID2();
      contentFilterRuleToAdd.name = Utils.generateUniqueEntityName(
        contentFilterRuleToAdd.name,
        _.map(this.contentFilterRules, 'name'),
        true
      );
      const url = `conf/${this.selectedBranch}/content-filter-rules/${contentFilterRuleToAdd.id}`;
      const postNewResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: contentFilterRuleToAdd,
        successMessage,
        failureMessage,
      });
      if (postNewResponse) {
        this.editContentFilterRule(contentFilterRuleToAdd);
      }
      this.setPageLoadingStatus(false);
    },

    async deleteContentFilterRule() {
      this.setPageLoadingStatus(true);
      const contentFilterRuleId = this.selectedContentFilterRule.id;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/content-filter-rules/${contentFilterRuleId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.contentFilterRules = _.filter(this.contentFilterRules, (contentFilterRule) => {
          return contentFilterRule.id !== contentFilterRuleId;
        });
      }
      this.showDeleteContentFilterRuleModal = false;
      this.setPageLoadingStatus(false);
    },

    downloadDoc() {
      Utils.downloadFile('content-filter-rules', 'json', this.contentFilterRules);
    },

    openDeleteContentFilterRuleModal(contentFilterRule: ContentFilterRule) {
      this.showDeleteContentFilterRuleModal = true;
      this.selectedContentFilterRule = contentFilterRule;
    },

    getNewContentFilterRulePath() {
      return this.getNewPath('content-filter-rules');
    },
  },
});
</script>
